import type {
  SolarEditableFinanceType,
  SolarFinancePartnerOptionDto,
  SolarFundingGroupOptionDto,
  SolarOfferingAccessoryDto,
  SolarOfferingInverterDto,
  SolarOfferingInverterGroupDto,
  SolarOfferingItemDto,
  SolarOfferingProgramDto,
  SolarProductOfferingPreviewDto,
  SolarProposalProductOfferingDto,
  SolarSystemType,
  UpdateSolarProductOfferingDto,
} from "~/api/dto/solar-proposal-product-offering.dto"

/**
 * What the seller changed. `undefined` is untouched, so the saved value
 * shows; `null` is a cleared optional selection.
 */
export type SolarEquipmentDraft = {
  financeType?: SolarEditableFinanceType
  /**
   * The plan's funding group (a group's `value`). `null` is a partner that
   * requires one (Sungage) drafted without it yet.
   */
  fundingGroup?: string | null
  loanProgramId?: number | null
  moduleId?: number
  inverterManufacturer?: string
  inverterId?: number | null
  secondaryInverterId?: number | null
  storageId?: number | null
  smartPanelId?: number | null
  evChargerId?: number | null
  showOptional?: boolean
  /** The Mount; the Summary's Equipment step edits it. */
  systemType?: SolarSystemType
}

export const EMPTY_EQUIPMENT_DRAFT: SolarEquipmentDraft = {}

/** A select's option: a catalog item, or "None" for an optional slot. */
export type SolarEquipmentOption = {
  id: number | null
  label: string
  recommended: boolean
}

export const NONE_OPTION: SolarEquipmentOption = {
  id: null,
  label: `None`,
  recommended: false,
}

/** One select as the form shows it. */
export type SolarEquipmentSlot = {
  options: SolarEquipmentOption[]
  value: SolarEquipmentOption | null
  /** A saved or drafted selection the preview no longer offers. */
  invalid: boolean
}

/** The plan the form edits: the drafted one, else the saved one while it is offered. */
export const selectedFinancePartner = (
  offering: SolarProposalProductOfferingDto,
  draft: SolarEquipmentDraft,
): SolarFinancePartnerOptionDto | null => {
  const financeType = draft.financeType ?? offering.partner.financeType
  return (
    offering.partners.find(
      (partner) => partner.available && partner.financeType === financeType,
    ) ?? null
  )
}

/** A drafted funding choice (partner, group, program) the Summary may count. */
export const draftedFundingChosen = (draft: SolarEquipmentDraft): boolean =>
  (draft.financeType !== undefined ||
    draft.loanProgramId !== undefined ||
    draft.fundingGroup !== undefined) &&
  draft.fundingGroup !== null

/** What a partner change drafts for the funding group. */
export const fundingGroupForPartnerChange = (
  partner: Pick<
    SolarFinancePartnerOptionDto,
    `financeType` | `requiresFundingGroup`
  >,
  savedFinanceType: string | null,
): Pick<SolarEquipmentDraft, `fundingGroup`> => ({
  // Back to the saved plan shows its saved group; another partner that
  // requires one starts without it.
  fundingGroup:
    partner.requiresFundingGroup === true &&
    partner.financeType !== savedFinanceType
      ? null
      : undefined,
})

/**
 * The plan's funding group, for a partner that requires one: the drafted
 * group, else the saved one, while the partner still offers it.
 */
export const selectedFundingGroup = (
  offering: SolarProposalProductOfferingDto,
  draft: SolarEquipmentDraft,
  partner: SolarFinancePartnerOptionDto | null,
): SolarFundingGroupOptionDto | null => {
  if (partner?.requiresFundingGroup !== true) return null
  const value =
    draft.fundingGroup !== undefined
      ? draft.fundingGroup
      : (offering.selectedFundingGroup ?? null)
  return (
    (partner.fundingGroups ?? []).find((group) => group.value === value) ?? null
  )
}

/** The saved partner when it is no longer offered: shown, with why, until another is drafted. */
export const unavailableSavedPartner = (
  offering: SolarProposalProductOfferingDto,
): SolarFinancePartnerOptionDto | null =>
  offering.partners.find(
    (partner) =>
      !partner.available && partner.financeType === offering.partner.financeType,
  ) ?? null

export const itemLabel = (
  item: Pick<SolarOfferingItemDto, `name` | `proposalName`>,
): string => item.proposalName?.trim() || item.name

const toOption = (
  item: SolarOfferingItemDto & { recommended?: boolean },
): SolarEquipmentOption => ({
  id: item.id,
  label: itemLabel(item),
  recommended: item.recommended === true,
})

const compatibleWith = (
  accessory: SolarOfferingAccessoryDto,
  inverterManufacturerId: number | null,
): boolean =>
  inverterManufacturerId === null ||
  accessory.compatibleInverterManufacturerIds.length === 0 ||
  accessory.compatibleInverterManufacturerIds.includes(inverterManufacturerId)

/**
 * A slot whose value must be one of `items`. A selection the items no longer
 * hold keeps showing, by its saved name when it is the saved one, and is
 * marked invalid.
 */
const slot = (
  items: SolarOfferingItemDto[],
  id: number | null,
  saved: { id: number | null; name: string | null },
  options: { optional: boolean },
): SolarEquipmentSlot => {
  const catalog = items.map(toOption)
  const selected = catalog.find((option) => option.id === id) ?? null
  const missingLabel =
    (id === saved.id ? saved.name : null) ?? `No longer available`
  return {
    options: options.optional ? [NONE_OPTION, ...catalog] : catalog,
    value:
      selected ??
      (id !== null
        ? { id, label: missingLabel, recommended: false }
        : options.optional
          ? NONE_OPTION
          : null),
    invalid: id !== null && selected === null,
  }
}

export type SolarEquipmentFormState = {
  partnerSelected: boolean
  /** The partner requires a funding group (Sungage). */
  fundingGroupRequired: boolean
  fundingGroups: SolarFundingGroupOptionDto[]
  fundingGroup: SolarFundingGroupOptionDto | null
  /** The saved group the partner no longer offers: it must be chosen again. */
  fundingGroupUnavailable: boolean
  programs: SolarOfferingProgramDto[]
  program: SolarOfferingProgramDto | null
  module: SolarEquipmentSlot
  manufacturers: SolarOfferingInverterGroupDto[]
  manufacturer: SolarOfferingInverterGroupDto | null
  /** The saved manufacturer's name when the preview offers none of its inverters. */
  manufacturerName: string | null
  inverter: SolarEquipmentSlot
  /** Shown only for a manufacturer V1 offers one for (SolarEdge). */
  secondary: SolarEquipmentSlot | null
  /** The inverter is itself a battery (Powerwall 3): no battery select. */
  batteryHidden: boolean
  /** The inverter takes no battery: an empty, disabled select. */
  batteryUnsupported: boolean
  battery: SolarEquipmentSlot
  smartPanel: SolarEquipmentSlot
  evCharger: SolarEquipmentSlot
  showOptional: boolean
  /** The Mount the save keeps: drafted, else saved. */
  systemType: SolarSystemType | null
  /** Optional selections the draft's program no longer offers; the save drops them. */
  dropped: string[]
  /** The PUT body, once a module and an inverter are picked. */
  body: UpdateSolarProductOfferingDto | null
  changed: boolean
  /** Every required selection is offered, so the save can be sent. */
  valid: boolean
}

/**
 * The form's view of the saved configuration, the seller's draft, and the
 * preview the draft keyed. Equipment the module rules out never appears as an
 * option; an inverter that is itself a battery hides and clears the battery;
 * accessories are filtered by inverter-manufacturer compatibility. An Aurora
 * design's module and inverters are the saved ones, whatever PowurOS offers.
 */
export const solarEquipmentFormState = (
  offering: SolarProposalProductOfferingDto,
  preview: SolarProductOfferingPreviewDto | null,
  draft: SolarEquipmentDraft,
): SolarEquipmentFormState => {
  const saved = offering.equipment
  const aurora = offering.moduleLocked || offering.invertersLocked
  const catalog = preview?.equipment ?? {
    modules: [],
    inverters: [],
    batteries: [],
    smartPanels: [],
    evChargers: [],
  }
  const partner = selectedFinancePartner(offering, draft)
  const partnerSelected = partner !== null
  const fundingGroupRequired = partner?.requiresFundingGroup === true
  const fundingGroup = selectedFundingGroup(offering, draft, partner)
  const fundingGroupUnavailable =
    fundingGroupRequired &&
    fundingGroup === null &&
    draft.fundingGroup === undefined &&
    partner?.financeType === offering.partner.financeType &&
    (offering.selectedFundingGroup ?? null) !== null
  const fundingGroupMet = !fundingGroupRequired || fundingGroup !== null

  const programs = preview?.programs ?? []
  const program =
    programs.find((entry) => entry.id === preview?.effectiveProgramId) ?? null
  // Battery's rule: a program is needed only when one is required or a default.
  const programNeeded = programs.some(
    (entry) => entry.required || entry.isDefault,
  )

  const moduleId = aurora
    ? (saved?.moduleId ?? null)
    : (draft.moduleId ?? saved?.moduleId ?? null)
  const { modules } = catalog
  // Aurora sets the module and inverters, so they are never flagged or dropped
  // for being off PowurOS's list; the server checks they are the saved ones.
  const core = (entry: SolarEquipmentSlot): SolarEquipmentSlot =>
    aurora ? { ...entry, invalid: false } : entry
  const moduleSlot = core(
    slot(
      modules,
      moduleId,
      { id: saved?.moduleId ?? null, name: saved?.moduleName ?? null },
      { optional: false },
    ),
  )

  const manufacturerName = aurora
    ? (saved?.inverterManufacturer ?? null)
    : (draft.inverterManufacturer ?? saved?.inverterManufacturer ?? null)
  const manufacturer =
    catalog.inverters.find((group) => group.manufacturer === manufacturerName) ??
    null
  const inverterId = aurora
    ? (saved?.inverterId ?? null)
    : draft.inverterId !== undefined
      ? draft.inverterId
      : (saved?.inverterId ?? null)
  // The module's incompatible inverters never appear as options.
  const models: SolarOfferingInverterDto[] = (manufacturer?.models ?? []).filter(
    (model) =>
      moduleId === null || !model.incompatibleWithModuleIds.includes(moduleId),
  )
  const inverterSlot = core(
    slot(
      models,
      inverterId,
      { id: saved?.inverterId ?? null, name: saved?.inverterName ?? null },
      { optional: false },
    ),
  )
  const inverter = models.find((model) => model.id === inverterId) ?? null

  const secondaryId = aurora
    ? (saved?.secondaryInverterId ?? null)
    : draft.secondaryInverterId !== undefined
      ? draft.secondaryInverterId
      : (saved?.secondaryInverterId ?? null)
  const secondary =
    manufacturer?.secondaryAllowed || (aurora && secondaryId !== null)
      ? core(
          slot(
            models.filter((model) => model.id !== inverterId),
            secondaryId,
            {
              id: saved?.secondaryInverterId ?? null,
              name: saved?.secondaryInverterName ?? null,
            },
            { optional: true },
          ),
        )
      : null

  // An Aurora design's inverter is judged by its saved flags, as the save
  // judges it, whether or not PowurOS still lists it.
  const locked = aurora ? offering.lockedInverter : null
  const batteryHidden = locked ? locked.isBattery : inverter?.isBattery === true
  const batteryUnsupported =
    !batteryHidden &&
    (locked
      ? !locked.isBatteryCompatible
      : inverter !== null && !inverter.isBatteryCompatible)
  const inverterManufacturerId = locked
    ? locked.manufacturerId
    : (manufacturer?.manufacturerId ?? null)
  const storageId = batteryHidden
    ? null
    : draft.storageId !== undefined
      ? draft.storageId
      : (saved?.storageId ?? null)
  const battery = slot(
    batteryUnsupported
      ? []
      : catalog.batteries.filter((entry) =>
          compatibleWith(entry, inverterManufacturerId),
        ),
    storageId,
    { id: saved?.storageId ?? null, name: saved?.storageName ?? null },
    { optional: true },
  )
  const smartPanelId =
    draft.smartPanelId !== undefined
      ? draft.smartPanelId
      : (saved?.smartPanelId ?? null)
  const smartPanel = slot(
    catalog.smartPanels.filter((entry) =>
      compatibleWith(entry, inverterManufacturerId),
    ),
    smartPanelId,
    { id: saved?.smartPanelId ?? null, name: saved?.smartPanelName ?? null },
    { optional: true },
  )
  const evChargerId =
    draft.evChargerId !== undefined
      ? draft.evChargerId
      : (saved?.evChargerId ?? null)
  const evCharger = slot(
    catalog.evChargers.filter((entry) =>
      compatibleWith(entry, inverterManufacturerId),
    ),
    evChargerId,
    { id: saved?.evChargerId ?? null, name: saved?.evChargerName ?? null },
    { optional: true },
  )

  // Only a current preview can say a selection is gone.
  const judged = preview !== null
  const keep = (
    selected: SolarEquipmentSlot,
    id: number | null,
  ): number | null => (judged && selected.invalid ? null : id)
  const dropped = judged
    ? [battery, smartPanel, evCharger, ...(secondary ? [secondary] : [])]
        .filter((entry) => entry.invalid)
        .map((entry) => entry.value?.label ?? ``)
    : []

  const coreInvalid = moduleSlot.invalid || inverterSlot.invalid
  // Sent only when drafted and different, so a save that never showed the
  // Mount (the Setup drawer) can't change it.
  const systemTypeChanged =
    draft.systemType !== undefined && draft.systemType !== offering.systemType
  const body: UpdateSolarProductOfferingDto | null =
    partner !== null &&
    fundingGroupMet &&
    moduleId !== null &&
    inverterId !== null &&
    manufacturerName !== null
      ? {
          financeType: partner.financeType,
          ...(fundingGroup ? { fundingGroup: fundingGroup.value } : {}),
          loanProgramId: program?.id ?? null,
          equipment: {
            moduleId,
            inverterManufacturer: manufacturerName,
            inverterId,
            secondaryInverterId: secondary ? keep(secondary, secondaryId) : null,
            storageId: keep(battery, storageId),
            smartPanelId: keep(smartPanel, smartPanelId),
            evChargerId: keep(evCharger, evChargerId),
          },
          ...(systemTypeChanged ? { systemType: draft.systemType } : {}),
        }
      : null

  const changed =
    body !== null &&
    (offering.partner.financeType !== body.financeType ||
      (fundingGroupRequired &&
        body.fundingGroup !== offering.selectedFundingGroup) ||
      body.loanProgramId !== offering.selectedLoanProgramId ||
      body.equipment.moduleId !== (saved?.moduleId ?? null) ||
      body.equipment.inverterManufacturer !==
        (saved?.inverterManufacturer ?? null) ||
      body.equipment.inverterId !== (saved?.inverterId ?? null) ||
      body.equipment.secondaryInverterId !==
        (saved?.secondaryInverterId ?? null) ||
      body.equipment.storageId !== (saved?.storageId ?? null) ||
      body.equipment.smartPanelId !== (saved?.smartPanelId ?? null) ||
      body.equipment.evChargerId !== (saved?.evChargerId ?? null) ||
      systemTypeChanged)

  return {
    partnerSelected,
    fundingGroupRequired,
    fundingGroups: fundingGroupRequired ? (partner?.fundingGroups ?? []) : [],
    fundingGroup,
    fundingGroupUnavailable,
    programs,
    program,
    module: moduleSlot,
    manufacturers: catalog.inverters,
    manufacturer,
    manufacturerName,
    inverter: inverterSlot,
    secondary,
    batteryHidden,
    batteryUnsupported,
    battery,
    smartPanel,
    evCharger,
    showOptional:
      draft.showOptional === true ||
      saved?.smartPanelId != null ||
      saved?.evChargerId != null,
    systemType: draft.systemType ?? offering.systemType,
    dropped,
    body,
    changed,
    valid:
      partnerSelected &&
      fundingGroupMet &&
      judged &&
      body !== null &&
      !coreInvalid &&
      (!programNeeded || program !== null),
  }
}
